/**
 * ICP (ideal-customer-profile) URL attribution — pure, stateless helpers.
 *
 * `icp` is a non-unique category supplied as a query param (`?icp=2`) that must
 * survive internal navigation by riding in the URL only. No cookies / storage /
 * identifiers are involved: the value is re-read from the live URL every time.
 *
 * Valid values are the integers 0..15 (hard allowlist). Anything else is treated
 * as absent and never propagated.
 */

const ICP_PARAM = "icp";

// 0..15, no leading zeros (rejects "02"), no sign, no decimals.
const ICP_PATTERN = /^(0|[1-9]|1[0-5])$/;

export function isValidIcp(v: string | null | undefined): v is string {
  return typeof v === "string" && ICP_PATTERN.test(v);
}

/**
 * Extract + validate the icp param from a search string ("?icp=2" / "icp=2")
 * or an already-parsed record. Returns the value only when it passes the
 * allowlist, otherwise null.
 */
export function readIcp(search: string | Record<string, string>): string | null {
  let raw: string | null;
  if (typeof search === "string") {
    raw = new URLSearchParams(
      search.startsWith("?") ? search.slice(1) : search,
    ).get(ICP_PARAM);
  } else {
    raw = search[ICP_PARAM] ?? null;
  }
  // A hand-built link that appends "?icp=" to a URL already carrying it reads
  // as "10?icp=10": keep the leading value instead of dropping the attribution.
  const value = raw?.split("?")[0];
  return isValidIcp(value) ? value : null;
}

/** Client convenience: the validated icp from the current URL (SSR-safe). */
export function currentIcp(): string | null {
  if (typeof window === "undefined") return null;
  return readIcp(window.location.search);
}

/**
 * `text` with an "ICP: n" line appended when the current URL carries an icp, so a
 * lead's profile reaches the sales inbox with it (the backend has no icp field).
 * Left as is without an icp, or when the tag would push it past `max` characters.
 */
export function tagIcp(text: string | undefined, max = Infinity): string | undefined {
  const icp = currentIcp();
  if (!icp) return text;
  const tagged = text ? `${text}\n\nICP: ${icp}` : `ICP: ${icp}`;
  return tagged.length > max ? text : tagged;
}

/**
 * True when `href` is a same-origin http(s) navigational link — the only kind
 * we ever attach icp to. Rejects external origins, mailto:/tel:, and pure hash
 * fragments (no destination change). `href` may be relative or absolute.
 */
function isInternalNavigable(href: string, origin: string): boolean {
  if (!href || href.startsWith("#")) return false;
  let url: URL;
  try {
    url = new URL(href, origin);
  } catch {
    return false;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return false;
  return url.origin === origin;
}

/**
 * Return `href` with `icp` merged in, or `href` unchanged when it should not be
 * touched. Preserves existing params, appends icp last, and never duplicates it
 * (idempotent: a link that already carries a valid icp is left as-is). `origin`
 * resolves relative hrefs and gates same-origin.
 */
export function mergeIcp(
  href: string,
  icp: string | null,
  origin: string,
): string {
  if (!isValidIcp(icp)) return href;
  if (!isInternalNavigable(href, origin)) return href;

  const url = new URL(href, origin);
  if (isValidIcp(url.searchParams.get(ICP_PARAM))) return href;
  url.searchParams.set(ICP_PARAM, icp);

  // Keep the output relative when the input was relative (no origin prefix).
  const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith("//");
  return isAbsolute ? url.href : url.pathname + url.search + url.hash;
}
